"use client";

// src/components/organisms/landing/landing-link-analytics.tsx
// One delegated `click` listener for the landing surface's `cta_click`
// and `outbound_click` events, so `hero.tsx`, `site-header.tsx` and
// `cta-button.tsx` can stay server components — this component only reads
// the `data-cta-placement` markers they render. Mounted once in
// `landing-shell.tsx`, which wraps the landing AND the `[...slug]` WordPress
// pages (the acquisition surface). Navigation is never touched:
// `preventDefault` is never called.
//
// The additive GA4 `commercial_cta_click` rides this same listener rather than
// adding a second click path. It is independent of the PostHog events above:
// its own placement marker (`data-commercial-cta`), its own destination rule,
// and a payload that shares no property with `cta_click`. Neither decides
// whether the other fires.

import { useEffect } from "react";
import { capture } from "@/lib/posthog/client";
import { EVENTS } from "@/lib/posthog/events";
import { ctaDestination, ctaLabel, isCtaPlacement, linkText, outboundHost } from "@/lib/posthog/link-events";
import { readTranslated } from "@/lib/posthog/translation";
import { COMMERCIAL_EVENTS, commercialCtaEvent } from "@/lib/analytics/commercial-events";
import { rememberReferringCta, sendGaEvent } from "@/lib/google-analytics";

export function LandingLinkAnalytics() {
  useEffect(() => {
    function onClick(event: MouseEvent) {
      const target = event.target as Element | null;
      const anchor = target?.closest?.("a[href]") ?? null;
      if (!anchor) return;

      const href = anchor.getAttribute("href") ?? "";
      const text = linkText(anchor.textContent);
      const translated = readTranslated();

      const placementEl = anchor.closest("[data-cta-placement]");
      const placement = placementEl?.getAttribute("data-cta-placement");
      if (isCtaPlacement(placement)) {
        const label = ctaLabel(placementEl?.getAttribute("data-cta-label"));
        capture(EVENTS.CTA_CLICK, {
          placement,
          ...(label === undefined ? {} : { cta_label: label }),
          cta_destination: ctaDestination(href),
          translated,
        });
      }

      const host = outboundHost(href, window.location.host);
      if (host !== null) {
        capture(EVENTS.OUTBOUND_CLICK, { href: host, link_text: text, translated });
      }

      // Driven by the destination, not by a list of CTAs to include: an href
      // that is not one of the three commercial destinations fires nothing,
      // which is what keeps "Sign in", /docs, "See Card Payments" and
      // internal blog links silent without naming them here.
      //
      // `data-commercial-cta` is this event's own marker and wins; the six
      // existing `data-cta-placement` markers are read as a fallback so the
      // header/hero/sticky CTAs need no markup change at all. `null` — no
      // marked ancestor at all — is what tells `commercialCtaEvent` that a
      // `t.me` link is site furniture (the floating support bubble, the
      // footer, the FAQ contact row) rather than an in-content CTA.
      const commercialEl = anchor.closest("[data-commercial-cta], [data-cta-placement]");
      const marker = commercialEl
        ? commercialEl.getAttribute("data-commercial-cta") ??
          commercialEl.getAttribute("data-cta-placement")
        : null;
      // The source string where one exists (`data-cta-label`), falling back
      // to the link's own text for the CTAs that carry no label attribute —
      // a machine-translated label is a known cost of covering those, and
      // the 80-char cut in `ctaLabel` is what keeps it bounded.
      const commercial = commercialCtaEvent(
        href,
        marker,
        ctaLabel(commercialEl?.getAttribute("data-cta-label")) ?? text,
      );
      if (commercial !== null) {
        sendGaEvent(COMMERCIAL_EVENTS.CTA_CLICK, commercial);
        // Survives the navigation to /sign-up, where `sign_up` reads it back
        // as `referring_cta_placement`.
        if (commercial.cta_destination_type === "signup") {
          rememberReferringCta(commercial.cta_placement);
        }
      }
    }

    // `click` only. A middle-click fires `auxclick`, not `click`, so opening
    // a CTA in a background tab with the middle button is a known one-sided
    // under-count — the weaker signal for a "clicked" metric — and listening
    // for both would need a `button === 1` filter to avoid counting the same
    // intent twice. Documented rather than hidden.
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return null;
}
